import { useState } from 'react';
import { NJButton, NJIcon, NJText } from '@engie-group/fluid-design-system-react';
import { RatioSectionHeader } from './RatioSectionHeader';
import { DegreeField, DegreePair, DegreePairFields } from './DegreeFields';
import { fmt, isValidDjclim, isValidDju } from './domain';
import { parseNum } from './sheet';
import type { DegreeDays, WeatherStation } from './types';
import './ratios.css';

interface Props {
  station: WeatherStation;
  degrees: DegreeDays;
  onDegreesChange: (next: DegreeDays) => void;
  onValidityChange: (ok: boolean) => void;
  onEditingChange: (editing: boolean) => void;
}

export function StationDegreesCard(p: Props) {
  const [editing, setEditing] = useState(false);
  const [dju, setDju] = useState(String(p.degrees.dju));
  const [djclim, setDjclim] = useState(String(p.degrees.djclim));
  const [beforeEdit, setBeforeEdit] = useState<DegreeDays>(p.degrees);
  const djuValid = isValidDju(parseNum(dju));
  const climValid = isValidDjclim(parseNum(djclim));
  const overridden = p.degrees.dju !== p.station.dju || p.degrees.djclim !== p.station.djclim;
  const setMode = (on: boolean) => { setEditing(on); p.onEditingChange(on); };
  const open = () => { setBeforeEdit({ ...p.degrees }); setDju(String(p.degrees.dju)); setDjclim(String(p.degrees.djclim)); setMode(true); };
  const cancel = () => {
    p.onDegreesChange(beforeEdit);
    setDju(String(beforeEdit.dju)); setDjclim(String(beforeEdit.djclim));
    p.onValidityChange(true); setMode(false);
  };
  const editDju = (value: string) => {
    setDju(value);
    const n = parseNum(value);
    if (isValidDju(n)) p.onDegreesChange({ ...p.degrees, dju: n });
    p.onValidityChange(isValidDju(n) && climValid);
  };
  const editClim = (value: string) => {
    setDjclim(value);
    const n = parseNum(value);
    if (isValidDjclim(n)) p.onDegreesChange({ ...p.degrees, djclim: n });
    p.onValidityChange(djuValid && isValidDjclim(n));
  };
  const reset = () => {
    p.onDegreesChange({ dju: p.station.dju, djclim: p.station.djclim });
    setDju(String(p.station.dju)); setDjclim(String(p.station.djclim));
    p.onValidityChange(true);
  };
  return (
    <section className="pcv3-ratios-card" aria-label="Climat cible du calcul">
      <RatioSectionHeader number={2} icon="thermostat" title="Climat cible station météo de référence" />
      <div className="pcv3-ratios-body">
        <div className="pcv3-station-climate-row">
        <div className="pcv3-weather-station" aria-label="Station météo sélectionnée">
          <NJIcon name="thermostat" />
          <div>
            <NJText scale="sm">Station météo</NJText>
            <NJText className="pcv3-weather-station-name">{p.station.name}</NJText>
          </div>
        </div>
        {editing ? <DegreePairFields>
          <DegreeField label="DJU simulés" text={dju} valid={djuValid} range={`500 à 5 000 ; initialement ${fmt(p.station.dju)}`} onChange={editDju} />
          <DegreeField label="DJClim simulés" text={djclim} valid={climValid} range={`10 à 1 000 ; initialement ${fmt(p.station.djclim)}`} onChange={editClim} />
        </DegreePairFields> : <DegreePair dju={p.degrees.dju} djclim={p.degrees.djclim} highlight={overridden} />}
        <div className="pcv3-ratios-input-actions">
        {editing
          ? <>
            <NJButton label="Valider" disabled={!djuValid || !climValid} onClick={() => setMode(false)} />
            <NJButton label="Annuler" variant="secondary" onClick={cancel} />
          </>
          : <NJButton label="Modifier" icon="edit" variant="secondary" onClick={open} />}
        {overridden && <NJButton label="Réinitialiser la simulation" variant="secondary" emphasis="minimal" onClick={reset} />}
        </div>
        </div>
        <div className="pcv3-climate-formulas" aria-label="Utilisation des degrés-jours dans le calcul">
          <NJText scale="sm"><strong>Chauffage :</strong> ratio de référence × (DJU de la station / DJU de référence).</NJText>
          <NJText scale="sm"><strong>Froid :</strong> ratio de référence × (DJClim de la station / DJClim de référence).</NJText>
          <NJText scale="sm"><strong>ECS :</strong> ratio de référence inchangé.</NJText>
          <NJText scale="sm">Chaque période conserve son coefficient indépendant par poste. Le calcul adapte les ratios de chaque période au climat de la station, sans appliquer une seconde correction à la consommation.</NJText>
        </div>
      </div>
    </section>
  );
}
